import {useEffect, useMemo, useRef, useState} from 'react';
import {observer} from 'mobx-react-lite';

import FeatureDiscoveryOrganism from '@sdb/ui/components/organisms/FeatureDiscovery/index';
import FeatureDiscoveryStore from '@sdb/ui/client-store/featureDiscovery.store';
import {I18nTransAtomComponent} from '@sdb/ui/translate/atoms/Trans/Trans.atom';
import {useTranslation} from '@sdb/ui/translate/hooks/index';

// Two independent tours, both anchored to the new dashboard (which only exists on the root route
// when the dashboard toggle is on). Bump these keys to re-show a tour to everyone.
//
// - Intro tour (restartFeatureDiscovery button): the dashboard toggle + the Catalog menu item.
// - Dashboard tour (circle-question button): the sections inside the new dashboard.
const INTRO_TOUR_ID = 'lp-intro-tour-2026-09';
const DASHBOARD_TOUR_ID = 'lp-dashboard-tour-2026-09';

// The dashboard toggle persists the user's choice under this key (see aca-main-page.js).
const NEW_DASHBOARD_STORAGE_KEY = 'useNewDashboard';

// Own store instances/tour ids so completion is tracked independently from the 'sdb-ui' tour.
//
// The intro tour greets the user on the classic `sdb-dashboard`. We force `open = false` after
// construction (the constructor sets `open = !viewed`) so that LearningWrapper can open it via
// restart(): restart() early-returns when `open` is already true, and — crucially — only that path
// sets `viewed = false`, which stops the tour organism from syncing with the server and silently
// marking it viewed without ever showing it.
export const introFeatureDiscoveryStore = new FeatureDiscoveryStore(INTRO_TOUR_ID);
introFeatureDiscoveryStore.open = false;

// The dashboard tour must NOT auto-open on load: it should only appear *after* the user switches to
// the new dashboard. The store constructor sets `open = !viewed`, so we immediately close it again
// here. It is (re)opened explicitly from `aca-main-page._toggleDashboard` (via `start()` on switch)
// and from the help button (via `restart()`). The DashboardTour component still waits for
// `sdb-dashboard-new` and its first target to mount before rendering.
export const dashboardFeatureDiscoveryStore = new FeatureDiscoveryStore(DASHBOARD_TOUR_ID);
dashboardFeatureDiscoveryStore.open = false;

/**
 * The dashboard targets are nested several shadow roots deep
 * (document → aca-app → aca-main-page → sdb-dashboard-new), which a plain
 * document.querySelector cannot pierce. This walks shadow roots recursively.
 */
const deepQuerySelector = (
    selector: string,
    root: Document | ShadowRoot = document,
): Element | null => {
    const direct = root.querySelector(selector);
    if (direct) return direct;

    for (const el of root.querySelectorAll('*')) {
        if (el.shadowRoot) {
            const found = deepQuerySelector(selector, el.shadowRoot);
            if (found) return found;
        }
    }
    return null;
};

const tourIdSelector = (tourId: string) => () => deepQuerySelector(`[data-tourid="${tourId}"]`);

// Navigation rail items are tagged with their (translated) label; fall back to the whole rail.
const navigationRailItemSelector = (label: string) => () =>
    deepQuerySelector(
        `[data-tourid="NavigationRailListItem-${label.trim().split(' ').join('-')}"]`,
    ) ?? deepQuerySelector("[data-tourid='NavigationRailList']");

const isNewDashboardActive = () =>
    window.localStorage.getItem(NEW_DASHBOARD_STORAGE_KEY) === 'true';

export const isNewDashboardActiveNow = isNewDashboardActive;

type TourStep = React.ComponentProps<typeof FeatureDiscoveryOrganism>['steps'][number];

/**
 * Shared renderer for a dashboard-anchored tour. Each tour belongs to exactly one dashboard, so we
 * gate on the presence of that dashboard's custom element in the DOM (`dashboardTag`) rather than a
 * localStorage flag: the toggle can swap dashboards in-session and the shared `dashboard-toggle`
 * target exists on both, so reading storage was both racy and ambiguous. The readiness loop waits
 * for both the matching dashboard element and the tour's own first target (the new dashboard is
 * lazily imported and mounts after the toggle), then flags ready. Re-checks whenever the tour opens
 * (auto on load via the store constructor, or via a restart() from a button).
 */
const DashboardTour = observer(
    ({
        store,
        steps,
        dashboardTag,
    }: {
        store: FeatureDiscoveryStore;
        steps: TourStep[];
        dashboardTag: string;
    }) => {
        const {t} = useTranslation();
        const [ready, setReady] = useState(false);
        const open = store.open;

        // Keep the latest steps in a ref so the polling effect doesn't tear down and re-run every
        // render (steps is a fresh array each render). Re-subscribing every frame + setReady every
        // frame is what caused the "Maximum update depth exceeded" loop.
        const stepsRef = useRef(steps);
        stepsRef.current = steps;

        useEffect(() => {
            if (!open) {
                setReady(false);
                return;
            }

            let frame = 0;
            // Poll continuously (not just until first found): the dashboard toggle swaps
            // sdb-dashboard <-> sdb-dashboard-new in-session, so `ready` must follow the currently
            // mounted dashboard. Only update state when the value actually changes to avoid an
            // infinite render loop.
            const check = () => {
                const dashboardPresent = deepQuerySelector(dashboardTag);
                const firstStepSelector = stepsRef.current[0]?.selector;
                const next = Boolean(dashboardPresent && firstStepSelector?.());

                setReady((prev) => (prev === next ? prev : next));
                frame = window.requestAnimationFrame(check);
            };
            check();

            return () => window.cancelAnimationFrame(frame);
        }, [open, dashboardTag]);

        if (!ready) return null;

        return (
            <FeatureDiscoveryOrganism
                open={store.open}
                onFinish={() => store.stop()}
                featureDiscoveryName={store.tourId}
                tourVersion="1"
                steps={steps}
                viewed={store.viewed}
                setViewed={store.setViewed}
                text={{
                    previous: t('featureDiscovery.previous'),
                    next: t('featureDiscovery.next'),
                    done: t('featureDiscovery.done'),
                    pagination: ({currentStep, totalSteps}) =>
                        t('featureDiscovery.pagination', {
                            currentStep: currentStep + 1,
                            totalSteps,
                        }),
                }}
            />
        );
    },
);

// Intro tour (restartFeatureDiscovery button): the dashboard toggle and the Catalog menu item.
const IntroFeatureDiscovery = observer(() => {
    const {t} = useTranslation();

    const steps: TourStep[] = useMemo(
        () => [
            {
                id: 'introToggle',
                selector: tourIdSelector('dashboard-toggle'),
                title: (
                    <I18nTransAtomComponent i18nKey="dashboardFeatureDiscovery.steps.toggle.title" />
                ),
                description: (
                    <I18nTransAtomComponent i18nKey="dashboardFeatureDiscovery.steps.toggle.description" />
                ),
            },
            {
                id: 'introCatalog',
                selector: navigationRailItemSelector(t('modules.mainMenu.labels.catalog')),
                description: (
                    <I18nTransAtomComponent i18nKey="dashboardFeatureDiscovery.steps.catalog.description" />
                ),
            },
        ],
        [t],
    );

    return (
        <DashboardTour
            store={introFeatureDiscoveryStore}
            steps={steps}
            dashboardTag="sdb-dashboard"
        />
    );
});

// Dashboard tour (circle-question button): the sections inside the new dashboard.
const DashboardFeatureDiscovery = observer(() => {
    // Auto-show the dashboard tour when the new dashboard is the active one and the user hasn't
    // seen it yet. `_toggleDashboard` only fires at the moment of switching, so a user who already
    // persisted the new dashboard (from a previous session) would otherwise never get it.
    //
    // Use restart() rather than start(): start() leaves `viewed` undefined, which makes the
    // organism sync with the server-side product-tour data and silently call setViewed() (marking
    // it 'true' in localStorage) *without ever rendering the tour*. restart() sets `viewed = false`
    // explicitly, disabling that sync so the tour actually shows (this is exactly how the main
    // 'sdb-ui' tour behaves in LearningWrapper). DashboardTour still waits for `sdb-dashboard-new`
    // and its first target to mount before actually rendering.
    useEffect(() => {
        if (isNewDashboardActive() && !dashboardFeatureDiscoveryStore.viewed) {
            dashboardFeatureDiscoveryStore.restart();
        }
    }, []);

    const steps: TourStep[] = useMemo(
        () => [
            {
                id: 'dashboardSearch',
                selector: tourIdSelector('dashboard-search'),
                description: (
                    <I18nTransAtomComponent i18nKey="dashboardFeatureDiscovery.steps.search.description" />
                ),
            },
            {
                id: 'dashboardProgress',
                selector: tourIdSelector('dashboard-progress'),
                description: (
                    <I18nTransAtomComponent i18nKey="dashboardFeatureDiscovery.steps.progress.description" />
                ),
            },
            {
                id: 'dashboardContinueLearning',
                selector: tourIdSelector('dashboard-continue-learning'),
                description: (
                    <I18nTransAtomComponent i18nKey="dashboardFeatureDiscovery.steps.continueLearning.description" />
                ),
            },
            {
                id: 'dashboardInformation',
                selector: tourIdSelector('dashboard-information'),
                description: (
                    <I18nTransAtomComponent i18nKey="dashboardFeatureDiscovery.steps.information.description" />
                ),
            },
        ],
        [],
    );

    return (
        <>
            <IntroFeatureDiscovery />
            <DashboardTour
                store={dashboardFeatureDiscoveryStore}
                steps={steps}
                dashboardTag="sdb-dashboard-new"
            />
        </>
    );
});

export default DashboardFeatureDiscovery;
